import { Link } from 'react-router-dom'
import { LayoutGrid } from 'lucide-react'
import { RAIL_GROUPS, type NavItem } from '../../config/navigation'

interface IconRailProps {
  visibleMainNav: NavItem[]
  activeSection: NavItem | null
  hoveredRailItem: string | null
  onHoverItem: (id: string | null, rect?: DOMRect) => void
  onClickItem: (item: NavItem) => void
}

export default function IconRail({
  visibleMainNav,
  activeSection,
  hoveredRailItem,
  onHoverItem,
  onClickItem,
}: IconRailProps) {
  // Mouse leave the rail → signal parent to start close timeout
  const handleMouseLeave = () => {
    onHoverItem(null)
  }

  // Mouse enter a rail item → signal parent with id + position
  const handleItemMouseEnter = (id: string, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    onHoverItem(id, rect)
  }

  // Flatten RAIL_GROUPS into ordered items with group boundaries.
  // Only keep items present in visibleMainNav (access gating); drop empty groups.
  const railItemGroups = RAIL_GROUPS.map((group) =>
    group.items
      .map((id) => visibleMainNav.find((item) => item.id === id))
      .filter((item): item is NavItem => item !== undefined)
  ).filter((groupItems) => groupItems.length > 0)

  return (
    <nav
      className="sticky top-0 flex flex-col items-center bg-white border-r border-light-border overflow-y-auto"
      style={{ width: 73, minWidth: 73, height: 'calc(100vh - 56px)' }}
      onMouseLeave={handleMouseLeave}
    >
      {/* Projects switcher */}
      <div className="flex items-center justify-center w-full py-3 flex-shrink-0">
        <Link
          to="/projects"
          className="flex items-center justify-center h-9 w-9 rounded-lg text-[#555A73] hover:bg-[#F4F5FA] hover:text-[#6366F1] transition-colors"
          title="Changer de projet"
        >
          <LayoutGrid size={20} strokeWidth={1.5} fill="currentColor" />
        </Link>
      </div>

      {/* Separator after logo */}
      <hr className="w-full border-light-border flex-shrink-0" />

      {/* Rail groups */}
      <div className="flex flex-col items-center w-full flex-1">
        {railItemGroups.map((groupItems, groupIndex) => (
          <div key={groupIndex} className="flex flex-col items-center w-full">
            {groupItems.map((item) => {
              const isActive = activeSection?.id === item.id
              const isComingSoon = item.comingSoon === true

              const itemClasses = [
                'flex flex-col items-center gap-0.5 w-full py-2 px-1 relative transition-colors duration-150',
                isComingSoon
                  ? 'opacity-40 cursor-default'
                  : 'cursor-pointer',
                isActive
                  ? 'bg-orion-primary-soft text-orion-primary border-l-[3px] border-orion-primary'
                  : !isComingSoon
                  ? 'text-[#6c6e79] hover:bg-light-surface-hover hover:text-[#1B2559]'
                  : 'text-[#6c6e79]',
              ]
                .filter(Boolean)
                .join(' ')

              // Compensate padding-left when active border-l-[3px] to avoid layout shift
              const innerStyle = isActive ? { paddingLeft: 0 } : undefined

              const Icon = item.icon

              const content = (
                <div
                  className={itemClasses}
                  style={innerStyle}
                  title={isComingSoon ? 'Bientot disponible' : item.label}
                  onMouseEnter={!isComingSoon ? (e) => handleItemMouseEnter(item.id, e) : undefined}
                  onClick={
                    !isComingSoon
                      ? () => onClickItem(item)
                      : undefined
                  }
                  aria-current={isActive ? 'page' : undefined}
                  role={!isComingSoon ? 'button' : undefined}
                  tabIndex={!isComingSoon ? 0 : undefined}
                  onKeyDown={
                    !isComingSoon
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onClickItem(item)
                          }
                        }
                      : undefined
                  }
                >
                  <Icon size={20} strokeWidth={1.5} />
                  <span className="text-[11px] text-center leading-tight max-w-full px-0.5 w-full block" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {item.label}
                  </span>
                </div>
              )

              return <div key={item.id} className="w-full">{content}</div>
            })}

            {/* Separator between groups, not after last */}
            {groupIndex < railItemGroups.length - 1 && (
              <hr className="w-full border-light-border my-1 flex-shrink-0" />
            )}
          </div>
        ))}
      </div>
    </nav>
  )
}
